import React, {useState} from 'react';

import {ANALYTICS_MODULE} from 'analytics/AnalyticsTypes';
import {assert} from 'Asserts';
import * as BenchlingUtil from 'BenchlingUtil';
import {ProcessTaskSchema} from 'forms/form-designer/Constants';
import {formCreatorUrl} from 'forms/form-designer/FormDesignerDAO';
import {FormDesignerProcessTaskSchemasQuery} from 'forms/form-designer/graphql/__generated/FormDesignerProcessTaskSchemasQuery';
import {GET_ALL_PROCESS_TASK_SCHEMAS} from 'forms/form-designer/graphql/FormDesignerQueries';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import navigateToURL from 'lib/navigateToURL';
import clampEntryName from 'notebook/entry-templates/clampEntryName';
import TemplateCollectionDAO from 'notebook/entry-templates/collections/TemplateCollectionDAO';
import TemplateCollectionSelector from 'notebook/entry-templates/collections/TemplateCollectionSelector';
import notifications from 'notifications';
import Col from 'ui-library/bs/Col';
import FormGroup from 'ui-library/bs/FormGroup';
import Label from 'ui-library/bs/Label';
import Row from 'ui-library/bs/Row';
import SelectDropdown from 'ui-library/dropdowns/SelectDropdown';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import Menu from 'ui-library/menus/Menu';
import SearchableMenu from 'ui-library/menus/SearchableMenu';
import Modal from 'ui-library/modals/Modal';
import {BasicEvent} from 'util/Types';

import {TemplateCollectionGraphQL} from '../../../graphql-fragments/notebook/templates/__generated/TemplateCollectionGraphQL';

export interface CreateFormModalProps {
  onRequestClose: () => void;
  initialTemplateCollection?: TemplateCollectionGraphQL;
  initialTaskSchema?: ProcessTaskSchema;
}

export default function CreateFormModal(props: CreateFormModalProps): JSX.Element {
  const [name, setName] = useState('');
  const [templateCollection, setTemplateCollection] = useState(props.initialTemplateCollection);
  const [taskSchema, setTaskSchema] = useState(props.initialTaskSchema);

  function manageCollections(e: BasicEvent): void {
    const url = TemplateCollectionDAO.collectionsURL();
    BenchlingUtil.navigate(e, url);
    props.onRequestClose();
  }

  function isSubmitEnabled(): boolean {
    const hasName = name.trim().length > 0;
    const hasTemplateCollection = templateCollection != null;
    const hasTaskSchema = taskSchema !== undefined;
    return hasName && hasTemplateCollection && hasTaskSchema;
  }

  return (
    <Modal
      title="Create form"
      onRequestClose={props.onRequestClose}
      onRequestSubmit={() => {
        assert(taskSchema !== undefined, 'Submit button should be disabled if task schema is unspecified');
        assert(
          templateCollection !== undefined,
          'Submit button should be disabled if template collection is unspecified'
        );
        navigateToURL(formCreatorUrl(name, templateCollection.id, taskSchema.id));
        props.onRequestClose();
      }}
      buttons={[
        {text: 'Create', submit: true, disabled: !isSubmitEnabled()},
        {text: 'Manage Collections', onClick: manageCollections, asLink: true, pullLeft: true},
      ]}
      dataTestAttrs={{component: 'CreateFormModal', element: 'modal'}}
      analyticsModule={ANALYTICS_MODULE.FORMS}
      analyticsModalType="create_form"
    >
      <Row>
        <Col sm={7}>
          <FormGroup dataTestAttrs={{component: 'CreateFormModal', element: 'name'}}>
            <Label>Name</Label>
            <input
              type="text"
              value={name}
              onChange={(e) => {
                setName(clampEntryName(e.target.value));
              }}
              autoFocus={true}
            />
          </FormGroup>
        </Col>
        <Col sm={5}>
          <Label>Template Collection</Label>
          <TemplateCollectionSelector
            value={templateCollection}
            onChange={(collection) => {
              setTemplateCollection(collection);
            }}
          />
        </Col>
      </Row>
      <Row>
        <Col sm={12}>
          <Label>Task Schema</Label>
          <TaskSchemaSelector
            value={taskSchema}
            onSelect={(schema: ProcessTaskSchema) => {
              setTaskSchema(schema);
            }}
          />
        </Col>
      </Row>
    </Modal>
  );
}

export function TaskSchemaSelector(props: {
  value?: ProcessTaskSchema;
  onSelect: (schema: ProcessTaskSchema) => void;
}): JSX.Element | null {
  const schemaQueryResult = useBenchlingQuery<FormDesignerProcessTaskSchemasQuery>(
    GET_ALL_PROCESS_TASK_SCHEMAS,
    {
      fetchPolicy: 'cache-and-network',
      renderCachedDataWhenRefetching: true,
    }
  );

  if (schemaQueryResult.type === QueryResultType.PENDING) {
    return <CenteredHorizontalThrobber />;
  } else if (schemaQueryResult.type === QueryResultType.ERROR) {
    notifications.error('Could not load schemas');
    return null;
  }

  const schemas = schemaQueryResult.data.processTaskSchemasSupportingPlatformFeature.map((schema) => ({
    name: schema.name,
    value: schema,
  }));

  return (
    <SelectDropdown
      displayName={props.value?.name ?? ''}
      menu={<SearchableMenu width={Menu.WIDTHS.FULL} optionDatas={schemas} onSelect={props.onSelect} />}
    />
  );
}
